<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Payroll History</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="goTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Payroll History</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading payroll history...</p>
          </div>

          <div v-else-if="historyData">
            <!-- Statistics Cards -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                          <i class="mdi mdi-cash-multiple"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Total Payments</p>
                        <h4 class="mb-0">{{ historyData.statistics.totalPayments }}</h4>
                        <p class="text-muted mb-0 small">All time</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                          <i class="mdi mdi-currency-usd"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Total Earned</p>
                        <h4 class="mb-0">{{ formatCurrency(historyData.statistics.totalNet) }}</h4>
                        <p class="text-muted mb-0 small">Net pay</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                          <i class="mdi mdi-chart-line"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Average Net Pay</p>
                        <h4 class="mb-0">{{ formatCurrency(historyData.statistics.avgNet) }}</h4>
                        <p class="text-muted mb-0 small">Per month</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                          <i class="mdi mdi-calendar-clock"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Latest Payment</p>
                        <h6 class="mb-0">{{ formatDate(historyData.statistics.latestPayment) }}</h6>
                        <p class="text-muted mb-0 small">Most recent</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters & Actions -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-md-3">
                        <label class="form-label">Year</label>
                        <select v-model="filters.year" @change="loadHistory" class="form-select">
                          <option value="">All Years</option>
                          <option v-for="year in historyData.availableYears" :key="year" :value="year">
                            {{ year }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Month</label>
                        <select v-model="filters.month" @change="loadHistory" class="form-select">
                          <option value="">All Months</option>
                          <option value="1">January</option>
                          <option value="2">February</option>
                          <option value="3">March</option>
                          <option value="4">April</option>
                          <option value="5">May</option>
                          <option value="6">June</option>
                          <option value="7">July</option>
                          <option value="8">August</option>
                          <option value="9">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Status</label>
                        <select v-model="filters.status" @change="loadHistory" class="form-select">
                          <option value="">All Statuses</option>
                          <option value="Pending">Pending</option>
                          <option value="Processing">Processing</option>
                          <option value="Paid">Paid</option>
                          <option value="Failed">Failed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </div>

                      <div class="col-md-3 d-flex align-items-end">
                        <button @click="resetFilters" class="btn btn-secondary me-2">
                          <i class="mdi mdi-refresh me-1"></i>
                          Reset
                        </button>
                        <button @click="exportHistory" class="btn btn-success">
                          <i class="mdi mdi-file-export me-1"></i>
                          Export
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Years Summary -->
            <div class="row" v-if="historyData.yearsSummary && historyData.yearsSummary.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-chart-bar text-primary me-2"></i>
                      Yearly Summary
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-hover mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Year</th>
                          <th class="text-center">Payments</th>
                          <th class="text-end">Total Gross</th>
                          <th class="text-end">Total Deductions</th>
                          <th class="text-end">Total Net</th>
                          <th class="text-end">Avg. Monthly</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="year in historyData.yearsSummary" :key="year.year">
                          <td>
                            <strong>{{ year.year }}</strong>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ year.payment_count }}</span>
                          </td>
                          <td class="text-end">{{ formatCurrency(year.total_gross) }}</td>
                          <td class="text-end text-danger">{{ formatCurrency(year.total_deductions) }}</td>
                          <td class="text-end text-success">
                            <strong>{{ formatCurrency(year.total_net) }}</strong>
                          </td>
                          <td class="text-end text-info">
                            {{ formatCurrency(year.total_net / year.payment_count) }}
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment History Table -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-history text-primary me-2"></i>
                      Payment History
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="historyData.history && historyData.history.length > 0" class="table-responsive">
                      <table class="table table-hover table-nowrap align-middle mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Period</th>
                          <th>Payment Date</th>
                          <th class="text-end">Gross Salary</th>
                          <th class="text-end">Deductions</th>
                          <th class="text-end">Net Salary</th>
                          <th>Status</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="payment in historyData.history" :key="payment.id">
                          <td>
                            <div>
                              <h6 class="mb-1">{{ payment.period_name }}</h6>
                              <p class="text-muted mb-0 small">Ref: {{ payment.payment_reference || 'N/A' }}</p>
                            </div>
                          </td>
                          <td>
                            <span class="text-muted">{{ payment.payment_date_formatted }}</span>
                          </td>
                          <td class="text-end">
                            <strong>{{ formatCurrency(payment.gross_salary) }}</strong>
                          </td>
                          <td class="text-end text-danger">
                            {{ formatCurrency(payment.total_deductions) }}
                          </td>
                          <td class="text-end text-success">
                            <strong>{{ formatCurrency(payment.net_salary) }}</strong>
                          </td>
                          <td>
                              <span class="badge" :class="getStatusBadgeClass(payment.payment_status)">
                                {{ payment.payment_status }}
                              </span>
                          </td>
                          <td class="text-center">
                            <div class="btn-group" role="group">
                              <button
                                  @click="viewDetails(payment)"
                                  class="btn btn-sm btn-outline-primary"
                                  title="View Details">
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button
                                  @click="viewPayslip(payment)"
                                  class="btn btn-sm btn-outline-success"
                                  title="View Payslip">
                                <i class="mdi mdi-file-document"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <div v-else class="text-center py-5">
                      <i class="mdi mdi-cash-remove font-size-48 text-muted"></i>
                      <h5 class="mt-2">No payment history found</h5>
                      <p class="text-muted">Try adjusting your filters</p>
                    </div>

                    <!-- Pagination -->
                    <div v-if="historyData.history && historyData.history.length > 0" class="row mt-4">
                      <div class="col-sm-12 col-md-5">
                        <div class="dataTables_info">
                          Showing {{ ((historyData.pagination.currentPage - 1) * historyData.pagination.limit) + 1 }}
                          to {{ Math.min(historyData.pagination.currentPage * historyData.pagination.limit, historyData.pagination.totalRecords) }}
                          of {{ historyData.pagination.totalRecords }} entries
                        </div>
                      </div>
                      <div class="col-sm-12 col-md-7">
                        <div class="dataTables_paginate float-end">
                          <ul class="pagination pagination-rounded mb-0">
                            <li class="page-item" :class="{ disabled: historyData.pagination.currentPage <= 1 }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(historyData.pagination.currentPage - 1)">
                                <i class="mdi mdi-chevron-left"></i>
                              </a>
                            </li>
                            <li
                                v-for="page in getPageNumbers()"
                                :key="page"
                                class="page-item"
                                :class="{ active: page === historyData.pagination.currentPage, disabled: page === '...' }">
                              <a
                                  v-if="page !== '...'"
                                  class="page-link"
                                  href="javascript:void(0)"
                                  @click="changePage(page)">
                                {{ page }}
                              </a>
                              <span v-else class="page-link">{{ page }}</span>
                            </li>
                            <li class="page-item" :class="{ disabled: historyData.pagination.currentPage >= historyData.pagination.totalPages }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(historyData.pagination.currentPage + 1)">
                                <i class="mdi mdi-chevron-right"></i>
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>

    <!-- Transaction Details Modal -->
    <TransactionDetails
        ref="transactionDetailsModal"
        :transaction-id="selectedTransactionId"
    />

    <!-- Payslip Modal -->
    <Payslip
        ref="payslipModal"
        :transaction-id="selectedPayslipId"
    />

  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import TransactionDetails from "@/components/payroll/TransactionDetails.vue";
import Payslip from "@/components/payroll/Payslip.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeePayrollHistory",
  components: {
    NavigationBar,
    TransactionDetails,
    Payslip
  },

  data() {
    return {
      loading: false,
      historyData: null,
      filters: {
        year: '',
        month: '',
        status: '',
        page: 1
      },
      selectedTransactionId: null,
      selectedPayslipId: null
    };
  },

  mounted() {
    this.loadHistory();
  },

  methods: {
    async loadHistory() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeePayrollHistory");
        data.append("page", this.filters.page);
        data.append("limit", 20);

        if (this.filters.year) {
          data.append("year", this.filters.year);
        }

        if (this.filters.month) {
          data.append("month", this.filters.month);
        }

        if (this.filters.status) {
          data.append("status", this.filters.status);
        }

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.historyData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load payroll history');
        }
      } catch (error) {
        this.$toast?.error("Failed to load payroll history: " + error.message) ||
        console.error("Failed to load payroll history:", error);
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.filters = {
        year: '',
        month: '',
        status: '',
        page: 1
      };
      this.loadHistory();
    },

    changePage(page) {
      if (page < 1 || page > this.historyData.pagination.totalPages || page === '...') return;
      this.filters.page = page;
      this.loadHistory();
    },

    getPageNumbers() {
      const totalPages = this.historyData.pagination.totalPages;
      const currentPage = this.historyData.pagination.currentPage;

      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      if (currentPage <= 3) {
        return [1, 2, 3, 4, '...', totalPages - 1, totalPages];
      }

      if (currentPage >= totalPages - 2) {
        return [1, 2, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      }

      return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    },

    viewDetails(payment) {
      this.selectedTransactionId = payment.id;

      this.$nextTick(() => {
        if (this.$refs.transactionDetailsModal) {
          this.$refs.transactionDetailsModal.showModal(payment.id);
        }
      });
    },

    viewPayslip(payment) {
      this.selectedPayslipId = payment.id;

      this.$nextTick(() => {
        if (this.$refs.payslipModal) {
          this.$refs.payslipModal.showModal(payment.id);
        }
      });
    },

    async exportHistory() {
      try {
        this.$toast?.info("Generating export...") || console.log("Generating export...");

        // Prepare export data
        const exportData = this.historyData.history.map(payment => ({
          'Period': payment.period_name,
          'Payment Date': payment.payment_date_formatted,
          'Gross Salary': payment.gross_salary,
          'Total Deductions': payment.total_deductions,
          'Net Salary': payment.net_salary,
          'Status': payment.payment_status,
          'Payment Reference': payment.payment_reference || 'N/A'
        }));

        // Convert to CSV
        const csv = this.convertToCSV(exportData);

        // Download
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Payroll_History_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$toast?.success("Export downloaded successfully") ||
        alert("Export downloaded successfully");
      } catch (error) {
        this.$toast?.error("Export failed: " + error.message) ||
        alert("Export failed: " + error.message);
      }
    },

    convertToCSV(data) {
      if (!data || data.length === 0) return '';

      const headers = Object.keys(data[0]);
      const csvHeaders = headers.join(',');

      const csvRows = data.map(row => {
        return headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',');
      });

      return [csvHeaders, ...csvRows].join('\n');
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'Pending':
          return 'bg-warning';
        case 'Processing':
          return 'bg-info';
        case 'Paid':
          return 'bg-success';
        case 'Failed':
          return 'bg-danger';
        case 'Cancelled':
          return 'bg-dark';
        default:
          return 'bg-secondary';
      }
    },

    goTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.font-size-48 {
  font-size: 48px;
}

.card-animate {
  transition: all 0.3s ease;
}

.card-animate:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.75rem 1.5rem rgba(18, 38, 63, 0.03);
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .btn-group {
    display: flex;
    flex-direction: column;
  }

  .btn-group .btn {
    margin-bottom: 0.25rem;
  }
}
</style>